import React from 'react';

import { WidgetType } from '@/api/rest/resources/types/widget';
import { AccordionSection, AccordionWrapper } from '@/components/widgets/o/Accordion/Accordion';
import { KnowledgeCards } from '@/pages/shared/knowledge/components/KnowledgeCards';
import { useEarningsChartConfiguration } from '@/pages/shared/portfolio/pages/NaturalCapital/components/EarningsChart';
import { useLandUseDistributionChartConfiguration } from '@/pages/shared/portfolio/pages/NaturalCapital/components/LandUseDistributionChart';
import { FormRadioGroup } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/interactive-map/components/Controls';
import { HabitatIntactnessThreatsTableCard } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/HabitatIntactness/components/ThreatsTable/TableCard';
import { ReportsList } from '@/pages/shared/reports/components/ReportsList';

import { useRiskAggregatedOverviewConfiguration } from '../ClimateRisk/RiskAggregatedOverview';
// eslint-disable-next-line import/no-cycle
import { RiskChart } from '../ClimateRisk/RiskChart';
import { RiskInfo } from '../ClimateRisk/RiskInfo';
import { RiskOverview } from '../ClimateRisk/RiskOverview';
import { RiskSectorChart } from '../ClimateRisk/RiskSectorChart';
import { DetailsCard } from '../DetailsCard/DetailsCard';
import { IndicatorCard } from '../IndicatorCard/IndicatorCard';
import { useNatureHealthConfiguration } from '../NatureHealth/NatureHealth';
import { RankedByResiliencePlotGroupList, RankedByRiskPlotGroupList } from '../PlotList/RankingPlotLists';
import { ButtonWidget } from './a/Button/Button';
import { IconButtonWidget } from './a/Button/IconButton';
import { Calendar } from './a/Calendar/Calendar';
import { Icon } from './a/Icon/Icon';
import { Image } from './a/Image/Image';
import { MailLink } from './a/Link/Mail';
import { OlListWidget, UlListWidget } from './a/List/List';
import { PillWidget } from './a/Pill/Pill';
import { ProgressWidget } from './a/Progress/Progress';
import { ProgressBar } from './a/ProgressBar/ProgressBar';
import { StackWidget } from './a/Stack/Stack';
import { TextContainer } from './a/Text/Text';
import { useAdditionalPlotsFilterPillWidget } from './config/AdditionalPlotsFilterPillWidget';
import {
  useEcologicalIntegrityIndex,
  useFormSectionPlotLabelValues,
  usePlotGroupFiltersConfiguration,
  usePlotGroupFiltersManagementPanel,
  usePlotGroupSortConfiguration,
  useResilienceDriversCard,
  useStewardedValue,
  useTotalAssets,
  useTotalSize,
} from './config/index';
import { useFactWidget } from './config/useFactWidget';
import { useFormSectionPlots } from './config/useFormSectionPlots';
import {
  useTotalStocksHeld,
  useTotalStocksHeldValue,
  useTotalStocksIssued,
  useTotalStocksIssuedValue,
  useTotalStocksSold,
  useTotalStocksSoldValue,
} from './config/useStockCards';
import { useSummaryFromRecommendation } from './config/useSummaryFromRecommendation';
import { useTotalAccountValue } from './config/useTotalAccountValue';
import { ConfigurationFromHookRenderer } from './hooks/ConfigurationFromHookRenderer';
import { Currency } from './m/Currency/Currency';
import { SearchBar } from './m/Search/SearchBar';
import { TooltipContentWidget, TooltipTriggerWidget, TooltipWidget } from './m/Tooltip/Tooltip';
import { TrendWidget } from './m/Trend/Trend';
import { ArticleCard } from './o/ArticleCard/ArticleCard';
import { Card } from './o/Card/Card';
import { CompositeGraph } from './o/CompositeGraph/CompositeGraph';
import {
  DialogBase,
  DialogBodyWidget,
  DialogCloseWidget,
  DialogContentWidget,
  DialogFooterWidget,
  DialogHeaderWidget,
  DialogWidget,
} from './o/Dialog/Dialog';
import { IndicatorCardDialog } from './o/Dialog/IndicatorCard';
import { ClearFormButton, FormButtonItem, SubmitButton } from './o/Form/Button';
import { FormCalendar } from './o/Form/Calendar';
import { CheckboxWidget } from './o/Form/CheckBox';
import { Form } from './o/Form/Form';
import { FormRadioItem } from './o/Form/FormRadio';
import { FormTextInput } from './o/Form/FormTextInput';
import { Picker, PickerContent, PickerTrigger } from './o/Form/Picker';
import { FormSection } from './o/Form/Section';
import { LinkCard } from './o/LinkCard/LinkCard';
import { useHabitatIntactnessYearsRadioButtonGroupWidget } from './o/Map/hooks/useHabitatIntactnessYearsRadioButtonGroupWidget';
import { RecommendationCardWidget } from './o/RecommendationCard/RecommendationCard';
import { ReportCard } from './o/ReportCard/ReportCard';
import { ResilienceDriver } from './o/ResilienceDriver/ResilienceDriver';
import { SimpleTable } from './o/SimpleTable/SimpleTable';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const WIDGET_TYPE_COMPONENT_MAP: Partial<Record<WidgetType, React.ComponentType<any>>> = {
  [WidgetType.o_card]: Card,
  [WidgetType.o_project_indicator_card]: IndicatorCard,
  [WidgetType.o_details_graph_card]: DetailsCard,
  [WidgetType.o_simple_table]: SimpleTable,
  [WidgetType.o_composite_graph]: CompositeGraph,
  [WidgetType.o_form]: Form,

  [WidgetType.o_form_text_input]: FormTextInput,
  [WidgetType.o_form_radio_group]: FormRadioGroup,
  [WidgetType.o_form_radio_item]: FormRadioItem,
  [WidgetType.o_form_picker]: Picker,
  [WidgetType.o_form_picker_trigger]: PickerTrigger,
  [WidgetType.o_form_picker_content]: PickerContent,
  [WidgetType.o_form_section]: FormSection,
  [WidgetType.o_form_button]: FormButtonItem,
  [WidgetType.o_form_submit_button]: SubmitButton,
  [WidgetType.o_form_clear_button]: ClearFormButton,
  [WidgetType.o_form_checkbox]: CheckboxWidget,
  [WidgetType.o_form_calendar]: FormCalendar,
  [WidgetType.o_recommendation_default_card]: RecommendationCardWidget,
  [WidgetType.o_c_form_label_values_section]: ConfigurationFromHookRenderer(useFormSectionPlotLabelValues),
  [WidgetType.o_c_form_plots_section]: ConfigurationFromHookRenderer(useFormSectionPlots),
  [WidgetType.o_c_form_radio_group_habitat_intactness_years]: ConfigurationFromHookRenderer((props) =>
    useHabitatIntactnessYearsRadioButtonGroupWidget(props),
  ),

  [WidgetType.o_recommendation_summary]: ConfigurationFromHookRenderer((props) => useSummaryFromRecommendation(props)),
  [WidgetType.o_stewarded_value]: ConfigurationFromHookRenderer(useStewardedValue),
  [WidgetType.o_total_account_value]: ConfigurationFromHookRenderer(useTotalAccountValue),
  [WidgetType.o_total_assets]: ConfigurationFromHookRenderer(useTotalAssets),
  [WidgetType.o_total_size]: ConfigurationFromHookRenderer(useTotalSize),
  [WidgetType.o_ecological_integrity_index]: ConfigurationFromHookRenderer(useEcologicalIntegrityIndex),
  [WidgetType.o_fact_summary]: ConfigurationFromHookRenderer((props) => useFactWidget(props)),
  [WidgetType.o_total_stocks_issued]: ConfigurationFromHookRenderer(useTotalStocksIssued),
  [WidgetType.o_total_stocks_held]: ConfigurationFromHookRenderer(useTotalStocksHeld),
  [WidgetType.o_total_stocks_sold]: ConfigurationFromHookRenderer(useTotalStocksSold),
  [WidgetType.o_total_stocks_issued_value]: ConfigurationFromHookRenderer(useTotalStocksIssuedValue),
  [WidgetType.o_total_stocks_held_value]: ConfigurationFromHookRenderer(useTotalStocksHeldValue),
  [WidgetType.o_total_stocks_sold_value]: ConfigurationFromHookRenderer(useTotalStocksSoldValue),

  [WidgetType.o_dialog_base]: DialogBase,
  [WidgetType.o_dialog]: DialogWidget,
  [WidgetType.o_dialog_header]: DialogHeaderWidget,
  [WidgetType.o_dialog_content]: DialogContentWidget,
  [WidgetType.o_dialog_body]: DialogBodyWidget,
  [WidgetType.o_dialog_close]: DialogCloseWidget,
  [WidgetType.o_dialog_footer]: DialogFooterWidget,

  [WidgetType.o_reports_list]: ReportsList,
  [WidgetType.o_report_card]: ReportCard,

  [WidgetType.o_article_card]: ArticleCard,
  [WidgetType.o_link_card]: LinkCard,

  [WidgetType.o_accordion_wrapper]: AccordionWrapper,
  [WidgetType.o_accordion_section]: AccordionSection,

  [WidgetType.o_c_knowledge_cards]: KnowledgeCards,

  [WidgetType.o_c_plot_group_filters]: ConfigurationFromHookRenderer(usePlotGroupFiltersConfiguration),
  [WidgetType.o_c_plot_group_filters_management]: ConfigurationFromHookRenderer(usePlotGroupFiltersManagementPanel),
  [WidgetType.o_c_plot_group_sort]: ConfigurationFromHookRenderer(usePlotGroupSortConfiguration),
  [WidgetType.o_c_plot_group_additional_plots_filter_pills]: ConfigurationFromHookRenderer((props) =>
    useAdditionalPlotsFilterPillWidget(props),
  ),

  [WidgetType.o_c_nature_health_overview]: ConfigurationFromHookRenderer(useNatureHealthConfiguration),

  [WidgetType.o_c_risk_aggregated_overview]: ConfigurationFromHookRenderer(useRiskAggregatedOverviewConfiguration),
  [WidgetType.o_c_risk_sector_chart]: RiskSectorChart,
  [WidgetType.o_c_risk_overview]: RiskOverview,
  [WidgetType.o_c_risk_chart]: RiskChart,
  [WidgetType.o_c_risk_info]: RiskInfo,
  [WidgetType.o_c_earnings_chart]: ConfigurationFromHookRenderer(useEarningsChartConfiguration),
  [WidgetType.o_c_land_distribution_chart]: ConfigurationFromHookRenderer(useLandUseDistributionChartConfiguration),

  [WidgetType.o_c_resilience_drivers_card]: ConfigurationFromHookRenderer(useResilienceDriversCard),
  [WidgetType.o_c_resilience_driver]: ResilienceDriver,

  [WidgetType.o_c_indicator_card_dialog]: IndicatorCardDialog,

  [WidgetType.o_c_ranked_by_risk_plot_list]: RankedByRiskPlotGroupList,
  [WidgetType.o_c_ranked_by_resilience_plot_list]: RankedByResiliencePlotGroupList,

  [WidgetType.o_c_habitat_intactness_threats_table]: HabitatIntactnessThreatsTableCard,

  [WidgetType.m_tooltip]: TooltipWidget,
  [WidgetType.m_tooltip_content]: TooltipContentWidget,
  [WidgetType.m_tooltip_trigger]: TooltipTriggerWidget,
  [WidgetType.m_trend]: TrendWidget,
  [WidgetType.m_currency]: Currency,
  [WidgetType.m_icon_button]: IconButtonWidget,
  [WidgetType.m_search_bar]: SearchBar,

  [WidgetType.a_progress_bar]: ProgressBar,
  [WidgetType.a_icon]: Icon,
  [WidgetType.a_text]: TextContainer,
  [WidgetType.a_pill]: PillWidget,
  [WidgetType.a_button]: ButtonWidget,
  [WidgetType.a_stack]: StackWidget,
  [WidgetType.a_progress]: ProgressWidget,
  [WidgetType.a_ul_list]: UlListWidget,
  [WidgetType.a_ol_list]: OlListWidget,
  [WidgetType.a_calendar]: Calendar,
  [WidgetType.a_image]: Image,
  [WidgetType.a_mail_link]: MailLink,
};
